import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const OWNER = {
  name: "Back to Digital",
  address: "Via Monte San Genesio 11, 20158 Milano (MI)",
  vat: "P.IVA 11836140969",
};

const UPDATED = "24 settembre 2026";

const Section = ({ number, title, children }) => (
  <section className="space-y-3">
    <h2 className="font-display text-xl font-bold text-amber-100">
      {number}. {title}
    </h2>
    <div className="space-y-3 text-sm leading-relaxed text-emerald-100/75">{children}</div>
  </section>
);

const LegalLayout = ({ title, subtitle, children }) => (
  <div className="relative min-h-screen bg-[#06150F] text-[#FAF6EE]">
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-[380px] bg-[radial-gradient(ellipse_at_top,rgba(90,15,25,0.5),transparent_65%)]"
    />
    <SiteHeader />
    <main className="relative z-10 mx-auto max-w-3xl px-5 pb-16 pt-12 sm:px-8">
      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300/90">Avvento d'Oro</p>
      <h1 className="font-display mt-3 text-3xl font-bold text-amber-100 sm:text-4xl">{title}</h1>
      <p className="mt-2 text-sm text-emerald-100/60">{subtitle}</p>
      <div className="mt-10 space-y-9">{children}</div>
      <p className="mt-12 border-t border-amber-500/15 pt-6 text-xs text-emerald-100/50">
        Ultimo aggiornamento: {UPDATED} — Titolare: {OWNER.name}, {OWNER.address}, {OWNER.vat}
      </p>
    </main>
    <SiteFooter />
  </div>
);

export const TerminiPage = () => (
  <LegalLayout
    title="Termini e Condizioni"
    subtitle="Condizioni generali di utilizzo e di vendita del Calendario dell'Avvento digitale."
  >
    <Section number={1} title="Titolare e oggetto">
      <p>
        La piattaforma "Avvento d'Oro" (di seguito la "Piattaforma") è gestita da {OWNER.name},{" "}
        {OWNER.address}, {OWNER.vat} (di seguito il "Titolare").
      </p>
      <p>
        La Piattaforma offre in vendita Calendari dell'Avvento digitali: 24 caselle virtuali, apribili
        una al giorno dal 1 al 24 dicembre, contenenti sorprese digitali, voucher, sconti, regali,
        vantaggi ed esperienze messi a disposizione da attività e brand partner (di seguito i "Partner").
      </p>
    </Section>

    <Section number={2} title="Registrazione e account">
      <p>
        Per acquistare, attivare o utilizzare un Calendario è necessario creare un account con nome,
        email e password. L'utente è responsabile della riservatezza delle proprie credenziali e di
        ogni attività svolta tramite il proprio account.
      </p>
      <p>
        Il Titolare può sospendere o eliminare account utilizzati in modo fraudolento, abusivo o
        contrario ai presenti Termini.
      </p>
    </Section>

    <Section number={3} title="Acquisto, codici e attivazione">
      <p>
        L'acquisto di un Calendario genera un codice di attivazione personale. Il codice può essere
        utilizzato dall'acquirente oppure ceduto a terzi come regalo: chi lo attiva per primo ne
        diventa l'unico intestatario e il codice non è più riutilizzabile.
      </p>
      <p>
        Il prezzo è quello indicato sulla Piattaforma al momento dell'acquisto. Salvo diversa
        indicazione, i contenuti del Calendario sono fruibili esclusivamente in forma digitale
        attraverso l'area personale dell'utente.
      </p>
    </Section>

    <Section number={4} title="Funzionamento delle caselle">
      <p>
        Ogni casella si sblocca nel corrispondente giorno di dicembre (la casella 1 il 1° dicembre,
        la casella 24 il 24 dicembre). Le caselle dei giorni precedenti restano apribili anche in
        seguito, per tutta la durata di disponibilità del servizio.
      </p>
      <p>
        L'ordine e l'assegnazione delle sorprese possono variare da Calendario a Calendario: utenti
        diversi possono trovare contenuti equivalenti in giorni diversi, secondo le edizioni e le
        combinazioni predisposte dal Titolare.
      </p>
    </Section>

    <Section number={5} title="Sorprese e Partner">
      <p>
        Le sorprese (voucher, sconti, prodotti, esperienze, vantaggi) sono ideate, fornite ed erogate
        dai Partner. La Piattaforma agisce esclusivamente come intermediario digitale che mette in
        contatto l'utente con le offerte dei Partner.
      </p>
      <p>
        Modalità, tempi, luoghi e condizioni di fruizione di ciascuna sorpresa sono definiti dal
        Partner che la eroga e possono essere soggetti a disponibilità, prenotazione o ulteriori
        condizioni comunicate dal Partner stesso.
      </p>
    </Section>

    <Section number={6} title="Costi aggiuntivi: clausola specifica">
      <p>
        <strong className="text-amber-100">
          Eventuali costi aggiuntivi di qualsiasi natura — incluse, a titolo esemplificativo e non
          esaustivo, spese di spedizione di prodotti fisici, costi di consegna, costi di prenotazione,
          consumazioni o servizi extra, integrazioni, supplementi o altre spese richieste per la
          fruizione di una sorpresa — non sono a carico della Piattaforma né del Titolare.
        </strong>
      </p>
      <p>
        Tali costi, ove previsti, sono gestiti direttamente dal Partner che eroga la sorpresa e sono
        regolati esclusivamente nel rapporto tra l'utente e il Partner, secondo le condizioni da
        quest'ultimo comunicate. La Piattaforma non è parte di tale rapporto e non risponde di
        richieste economiche, addebiti o condizioni applicate dal Partner.
      </p>
      <p>
        Prima di fruire di una sorpresa, l'utente è invitato a verificare con il Partner l'eventuale
        presenza di costi aggiuntivi.
      </p>
    </Section>

    <Section number={7} title="Codici regalo e omaggio">
      <p>
        I codici contrassegnati come "regalo" sono destinati alla cessione a terzi; i codici
        contrassegnati come "omaggio" sono distribuiti gratuitamente per iniziative promozionali.
        In entrambi i casi il contenuto attivato corrisponde all'edizione di Calendario associata al
        codice, salvo diversa indicazione.
      </p>
    </Section>

    <Section number={8} title="Limitazione di responsabilità">
      <p>
        Il Titolare non risponde della qualità, disponibilità, legittimità o erogazione delle
        sorprese da parte dei Partner, né di eventuali danni derivanti dal rapporto tra l'utente e
        i Partner, ferma restando la propria responsabilità per il funzionamento della Piattaforma.
      </p>
      <p>
        La Piattaforma è fornita "così com'è": il Titolare non garantisce l'assenza di interruzioni
        o errori tecnici e si impegna a ripristinare il servizio in tempi ragionevoli.
      </p>
    </Section>

    <Section number={9} title="Diritto di recesso e contenuti digitali">
      <p>
        Ai sensi dell'art. 59, lett. o), del Codice del Consumo, per i contenuti digitali forniti
        mediante supporto non materiale, con l'attivazione del codice e l'accesso ai contenuti
        l'utente accetta l'esecuzione immediata del contratto e riconosce di perdere il diritto di
        recesso per i contenuti già attivati o già fruiti.
      </p>
      <p>
        Per codici mai attivati resta fermo il diritto di recesso entro 14 giorni dall'acquisto,
        esercitabile contattando il Titolare ai recapiti indicati.
      </p>
    </Section>

    <Section number={10} title="Proprietà intellettuale">
      <p>
        Grafica, testi, logo, animazioni e contenuti della Piattaforma sono di proprietà del Titolare
        o dei rispettivi Partner e non possono essere riprodotti senza autorizzazione.
      </p>
    </Section>

    <Section number={11} title="Modifiche, legge e foro competente">
      <p>
        Il Titolare può aggiornare i presenti Termini in qualsiasi momento; la versione vigente è
        quella pubblicata sulla Piattaforma. L'uso continuato del servizio costituisce accettazione
        delle modifiche.
      </p>
      <p>
        I presenti Termini sono regolati dalla legge italiana. Per ogni controversia è competente il
        Foro di Milano, salvo diversa previsione inderogabile a tutela del consumatore.
      </p>
    </Section>
  </LegalLayout>
);

export const PrivacyPage = () => (
  <LegalLayout
    title="Informativa Privacy"
    subtitle="Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679 (GDPR)."
  >
    <Section number={1} title="Titolare del trattamento">
      <p>
        Il Titolare del trattamento è {OWNER.name}, {OWNER.address}, {OWNER.vat}, contattabile
        tramite i recapiti pubblicati sulla Piattaforma e presso la sede indicata.
      </p>
    </Section>

    <Section number={2} title="Dati personali raccolti">
      <p>
        <strong className="text-amber-100">Dati dell'account:</strong> nome, indirizzo email e
        password (conservata esclusivamente in forma cifrata mediante algoritmo di hashing bcrypt).
      </p>
      <p>
        <strong className="text-amber-100">Dati di utilizzo:</strong> calendari attivati, codici di
        attivazione, caselle aperte, date di apertura, edizioni acquistate o ricevute.
      </p>
      <p>
        <strong className="text-amber-100">Dati newsletter:</strong> indirizzo email, fornito
        spontaneamente per ricevere comunicazioni.
      </p>
      <p>
        <strong className="text-amber-100">Contenuti caricati:</strong> immagini caricate dagli
        amministratori (loghi, foto dei premi, immagini per le email).
      </p>
      <p>
        <strong className="text-amber-100">Dati tecnici:</strong> log di accesso, indirizzo IP,
        informazioni sul dispositivo e sul browser, raccolti automaticamente per sicurezza e
        funzionamento del servizio.
      </p>
    </Section>

    <Section number={3} title="Finalità e base giuridica">
      <p>
        <strong className="text-amber-100">Erogazione del servizio</strong> (creazione account,
        attivazione calendari, apertura delle caselle, gestione dei codici): esecuzione di un
        contratto di cui l'utente è parte (art. 6.1.b GDPR).
      </p>
      <p>
        <strong className="text-amber-100">Newsletter e comunicazioni promozionali:</strong> consenso
        dell'interessato (art. 6.1.a GDPR), revocabile in qualsiasi momento.
      </p>
      <p>
        <strong className="text-amber-100">Sicurezza e prevenzione frodi</strong> (limitazione dei
        tentativi di accesso, log tecnici): legittimo interesse del Titolare (art. 6.1.f GDPR).
      </p>
      <p>
        <strong className="text-amber-100">Obblighi di legge</strong> (conservazione dei dati di
        ordine a fini fiscali e contabili): obbligo legale (art. 6.1.c GDPR).
      </p>
    </Section>

    <Section number={4} title="Modalità del trattamento e conservazione">
      <p>
        I dati sono trattati con strumenti informatici e misure di sicurezza adeguate: password
        cifrate, sessioni protette da cookie httpOnly non leggibili via script, accessi amministrativi
        riservati. I dati sono conservati per la durata del rapporto e, per i dati di ordine, per i
        termini di legge fiscali; i dati newsletter fino a revoca del consenso o cancellazione.
      </p>
    </Section>

    <Section number={5} title="Destinatari e fornitori">
      <p>
        I dati possono essere trattati da fornitori tecnici che operano come responsabili del
        trattamento: provider di hosting e infrastruttura cloud, servizio di invio email
        (Resend) per le comunicazioni, servizio di archiviazione sicura delle immagini, strumenti di
        analisi aggregata dell'utilizzo. I dati non sono venduti né comunicati a terzi per finalità
        proprie.
      </p>
      <p>
        Alcuni fornitori possono trattare dati al di fuori dello Spazio Economico Europeo: in tal
        caso il trasferimento avviene nel rispetto delle garanzie previste dal GDPR.
      </p>
    </Section>

    <Section number={6} title="Diritti dell'interessato">
      <p>
        L'utente può in qualsiasi momento esercitare i diritti di accesso, rettifica, cancellazione,
        limitazione, portabilità e opposizione al trattamento, nonché revocare il consenso alla
        newsletter, scrivendo al Titolare ai recapiti indicati. È inoltre possibile disiscriversi
        dalla newsletter tramite il pannello di gestione o richiedendolo al Titolare.
      </p>
      <p>
        Resta fermo il diritto di proporre reclamo al Garante per la protezione dei dati personali
        (www.garanteprivacy.it).
      </p>
    </Section>

    <Section number={7} title="Minori">
      <p>
        Il servizio è rivolto a utenti maggiori di 16 anni. Il Titolare non raccoglie
        consapevolmente dati di minori di 16 anni.
      </p>
    </Section>

    <Section number={8} title="Modifiche alla presente informativa">
      <p>
        La presente informativa può essere aggiornata nel tempo; la versione vigente è quella
        pubblicata su questa pagina con la relativa data di aggiornamento.
      </p>
    </Section>
  </LegalLayout>
);

export const CookiePage = () => (
  <LegalLayout
    title="Cookie Policy"
    subtitle="Informazioni sui cookie e sugli strumenti di tracciamento utilizzati dalla Piattaforma."
  >
    <Section number={1} title="Cosa sono i cookie">
      <p>
        I cookie sono piccoli file di testo che i siti visitati salvano sul dispositivo dell'utente
        per garantire funzionalità tecniche, sicurezza e — in alcuni casi — finalità statistiche o di
        profilazione.
      </p>
    </Section>

    <Section number={2} title="Cookie tecnici necessari">
      <p>
        La Piattaforma utilizza esclusivamente cookie tecnici indispensabili al funzionamento del
        servizio, per i quali non è richiesto il consenso:
      </p>
      <div className="overflow-x-auto rounded-xl border border-amber-500/20">
        <table className="w-full min-w-[480px] text-left text-sm">
          <thead>
            <tr className="border-b border-amber-500/15 bg-[#0C241A]/70 text-xs uppercase tracking-wider text-amber-300/80">
              <th className="px-4 py-3">Cookie</th>
              <th className="px-4 py-3">Finalità</th>
              <th className="px-4 py-3">Durata</th>
            </tr>
          </thead>
          <tbody className="text-emerald-100/75">
            <tr className="border-b border-amber-500/10">
              <td className="px-4 py-3 font-mono text-xs">access_token</td>
              <td className="px-4 py-3">Autenticazione della sessione (httpOnly, sicuro, non leggibile via script)</td>
              <td className="px-4 py-3">30 minuti</td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-mono text-xs">refresh_token</td>
              <td className="px-4 py-3">Rinnovo automatico della sessione (httpOnly, sicuro)</td>
              <td className="px-4 py-3">7 giorni</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>

    <Section number={3} title="Strumenti di terze parti">
      <p>
        La Piattaforma può utilizzare strumenti di analisi dell'utilizzo (ad esempio PostHog) per
        raccogliere statistiche aggregate e anonime sulle pagine visitate, finalizzate al miglioramento
        del servizio. Tali strumenti possono installare identificatori tecnici propri secondo le
        rispettive informative.
      </p>
      <p>
        La Piattaforma non utilizza cookie di profilazione pubblicitaria né cookie di terze parti per
        finalità di marketing.
      </p>
    </Section>

    <Section number={4} title="Gestione dei cookie dal browser">
      <p>
        L'utente può bloccare o eliminare i cookie dalle impostazioni del proprio browser. La
        disattivazione dei cookie tecnici di autenticazione impedisce l'accesso all'area personale e
        l'utilizzo delle funzioni riservate (apertura delle caselle, gestione dei codici).
      </p>
    </Section>

    <Section number={5} title="Aggiornamenti">
      <p>
        La presente Cookie Policy può essere aggiornata in caso di introduzione di nuovi strumenti;
        la versione vigente è quella pubblicata su questa pagina.
      </p>
    </Section>
  </LegalLayout>
);
